import { Radio, RadioGroup, Tooltip, Typography } from '@mui/material';
import { investment as rules } from 'further-ui/validations';
import { FC } from 'react';
import { Control, Controller } from 'react-hook-form';

import { InfoOutline } from '@mui/icons-material';
import { AssetSelectionType } from 'further-types/lp/investment';
import { CurrencyCode } from 'further-types/lp/vehicle';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormControlLabel from 'further-ui/components/FormControlLabel';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';

type AssetSelectionProps = {
  isNewAsset: boolean;
  assetOptions: Array<{
    id: string;
    title: string;
    baseCurrency?: CurrencyCode;
  }>;
  control: Control<rules.CreateInvestmentFormValues>;
  resetAssetDropdown: () => void;
  onAssetChange: (
    name: string,
    assetId?: string,
    baseCurrency?: CurrencyCode,
  ) => void;
};

const AssetSelection: FC<AssetSelectionProps> = ({
  isNewAsset,
  assetOptions,
  control,
  resetAssetDropdown,
  onAssetChange,
}) => (
  <Controller
    name="isNewAsset"
    control={control}
    defaultValue={false}
    render={({ field: { value, onChange, ...rest } }) => (
      <RadioGroup
        {...rest}
        value={value ? AssetSelectionType.NEW : AssetSelectionType.EXISTING}
        onChange={(e) => onChange(e.target.value === AssetSelectionType.NEW)}
      >
        <Column spacing="lg">
          <FormField
            label={
              <Row centered spacing="xxs">
                <FormControlLabel
                  value={AssetSelectionType.EXISTING}
                  control={<Radio />}
                  label={
                    <Typography variant="h6">Select existing asset</Typography>
                  }
                />
                <Tooltip title="Select an asset that already exists in the system.">
                  <InfoOutline fontSize="inherit" />
                </Tooltip>
              </Row>
            }
            size="md"
          >
            <Controller
              name="assetId"
              control={control}
              render={({
                field: { ref, value, ...rest },
                fieldState: { error },
              }) => (
                <BasicSelect
                  {...rest}
                  value={value}
                  onChange={(selectedAssetId) => {
                    rest.onChange(selectedAssetId);
                    const selectedAsset = assetOptions.find(
                      (asset) => asset.id === selectedAssetId,
                    );
                    onAssetChange(
                      selectedAsset?.title || '',
                      selectedAsset?.id,
                      selectedAsset?.baseCurrency,
                    );
                  }}
                  disabled={isNewAsset}
                  fullWidth
                  options={assetOptions.map((asset) => ({
                    value: asset.id,
                    label: asset.title,
                  }))}
                  placeholder="Select asset"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </FormField>

          <FormField size="md">
            <Row centered spacing="xxs">
              <FormControlLabel
                value={AssetSelectionType.NEW}
                control={<Radio />}
                label={<Typography variant="h6">Create new asset</Typography>}
                onClick={() => {
                  resetAssetDropdown();
                  onAssetChange('');
                }}
              />
              <Tooltip title=" Select if the asset is not yet in the system.">
                <InfoOutline fontSize="inherit" />
              </Tooltip>
            </Row>
          </FormField>
        </Column>
      </RadioGroup>
    )}
  />
);

export default AssetSelection;
